import {HiCheck as CheckIcon} from 'react-icons/hi2'
import {useCallback, useMemo} from 'react'
import {compact, uniqBy} from 'lodash-es'
import clsx from 'clsx'
import {ProductAppearance, ProductVariant} from '~/gql/graphql'

type AppearanceSelectorProps = {
    label: string
    variants: ProductVariant[]
    selectedAppearanceId: string
    onAppearanceSelect: (appearance: ProductAppearance) => void
}

const AppearanceSelector = ({
    label,
    variants,
    selectedAppearanceId,
    onAppearanceSelect,
}: AppearanceSelectorProps) => {
    const appearances = useMemo(() => {
        const appearances = variants?.map((variant) => variant?.appearance)
        return compact(uniqBy(appearances, 'id'))
    }, [variants])

    const isAvailableAppearance = useCallback(
        (appearanceId: string) =>
            variants.some(
                (variant) =>
                    variant.appearance?.id === appearanceId && (variant.stockQuantity ?? 0) > 0
            ),
        [variants]
    )

    return (
        <fieldset aria-label={label} className="flex flex-row flex-wrap gap-3 appearance-selector">
            {appearances.map((appearance) => {
                const isAvailable = isAvailableAppearance(appearance.id!)
                const isSelected = selectedAppearanceId === appearance.id
                const primaryColor = appearance.values?.[0]
                const secondaryColor = appearance.values?.[1]
                return (
                    <label
                        className="cursor-pointer"
                        aria-label={appearance.name!}
                        key={appearance.id!}
                        title={appearance.name!}
                    >
                        <input
                            type="radio"
                            name="appearance-choice"
                            value={appearance.id!}
                            checked={isAvailable && isSelected}
                            className="sr-only"
                            disabled={!isAvailable}
                            onChange={() => isAvailable && onAppearanceSelect(appearance)}
                        />
                        <div aria-hidden="true" className="relative">
                            <div
                                className={clsx(
                                    'relative rounded-full border shadow-lg size-10 overflow-hidden',
                                    isSelected && isAvailable
                                        ? 'border-comet ring-2 ring-comet'
                                        : 'border-white',
                                    !isAvailable && 'opacity-40'
                                )}
                                style={{backgroundColor: primaryColor ?? 'transparent'}}
                                data-appearance={appearance.id}
                            >
                                {!!secondaryColor && (
                                    <div
                                        className="absolute w-3 h-full rotate-45 right-0 -bottom-3"
                                        style={{
                                            backgroundColor: secondaryColor ?? 'transparent',
                                        }}
                                    />
                                )}
                            </div>
                            {isSelected && (
                                <div className="absolute -right-1.5 -top-1.5 size-5 rounded-full bg-comet">
                                    <CheckIcon className="size-full p-0.5" />
                                </div>
                            )}
                        </div>
                    </label>
                )
            })}
        </fieldset>
    )
}

export default AppearanceSelector
